import React, { useState, useEffect } from 'react'
import { FormattedMessage, FormattedDate, FormattedTime, useIntl } from 'react-intl'
import messages from './messages'
import { Table, DataTable, Button, Chip, Input, TextArea } from '@arnsbogroup/react-voicearchive'
import {runtime} from 'rest/runtime'
import DateTime from "react-datetime";
import BookingInfoModal from './components/BookingInfoModal'
import BookingRejectModal from './components/BookingRejectModal'

const InlineEventEditor = ({ event, onSaveInternal, onCancel, booking }) => {
  const { formatMessage } = useIntl()

  const [start, setStart] = useState(event ? new Date(event.dateStart * 1000) : new Date)
  const [end, setEnd] = useState(event ? new Date(event.dateEnd * 1000) : new Date)

  const [message, setMessage] = useState(event ? event.message : '')
  const [dealId, setDealId] = useState(event && event.bookingDetails ? event.bookingDetails.dealId : '')
  const [errors, setErrors] = useState(null)

  const onSubmit = (e) => {
    e.preventDefault()

    if (booking && message == '') {
      setErrors({ message: "Note must be filled out"})
      return
    }

    const now = new Date().getTime()
    if (start < now) {
      toastr.error("Start date can't be in the past" )
      return
    } else if (end < now || end < start) {
      toastr.error("End date can't be before start date or in the past" )
      return
    }

    const nextEvent = {
      ...event,
      dateStart: start / 1000,
      dateEnd: end / 1000,
      message,
    }

    if (booking) {
      nextEvent.bookingDetails = {
        dealId: dealId
      }
    }

    onSaveInternal(nextEvent)
  }

  return (
    <form className="bg-gray-100 border border-gray px-4 py-5 flex items-center space-x-2 text-sm mt-4" onSubmit={onSubmit}>
      <div className="w-3/12">
        <div className="text-xs font-medium text-gray-700 mb-2">
          <FormattedMessage {...messages.fromLabel } />
          <span className="text-gray-400 text-xs ml-2">(dd/mm/yyyy hh:mm)</span>
        </div>
        <DateTime
          value={start}
          inputProps={{
            className: "border-none"
          }}
          onChange={(value) => setStart(Math.round(new Date(value).getTime()))}
          className="rounded-sm border border-gray-300 p-1 sm:p-2 bg-white"
          closeOnTab
        />
      </div>
      <div className="w-3/12">
        <div className="text-xs font-medium text-gray-700 mb-2">
          <FormattedMessage {...messages.toLabel } />
          <span className="text-gray-400 text-xs ml-2">(dd/mm/yyyy hh:mm)</span>
        </div>
        <DateTime
          value={end}
          inputProps={{
            className: "border-none"
          }}
          onChange={(value) => setEnd(Math.round(new Date(value).getTime()))}
          className="rounded-sm border border-gray-300 p-1 sm:p-2 bg-white"
          closeOnTab
        />
      </div>
      <div className="w-5/12">
        <TextArea
          rows={1}
          value={message}
          optional={!booking}
          required={booking}
          size="sm"
          onChange={({ target: { value } }) => setMessage(value)}
          label={formatMessage(messages.availabilityMessageLabel)}
          labelClassName="text-gray-700 text-xs"
          placeholder={formatMessage(messages.availabilityMessagePlaceholder)}
          className="w-5/6 border border-gray-400 p-2"
          errors={errors ? [errors.message] : []}
        />
      </div>
      { runtime.currentUser.isCoordinator() && booking &&
        <div className="w-5/12">
          <Input
            value={dealId}
            optional
            size="sm"
            onChange={({ target: { value } }) => setDealId(value)}
            label={formatMessage(messages.bookingMessageLabel)}
            labelClassName="text-gray-700 text-xs"
            placeholder={formatMessage(messages.bookingMessagePlaceholder)}
            className="w-5/6 border border-gray-400 p-2"
          />
        </div>
      }
      <div className="flex justify-between items-center text-sm">
        <button type="submit" className="w-10 text-primary">
          <FormattedMessage {...messages.save } />
        </button>

        <button className="w-10 text-red-500" onClick={onCancel}>
          <FormattedMessage {...messages.cancel } />
        </button>
      </div>
    </form>
  )
}

const AvailabilityDataTable = ({ 
  noDataMessage = '',
  includeBookingRequest = false,
  includeAvailabilityRequest = false,
  availabilities = [],
  bookings = [],
  onSave = () => {},
  onDelete = () => {},
  bookingAction = () => {},
}) => {
  const [internalAvailabilities, setInternalAvailabilities] = useState(availabilities)
  const [event, setEvent] = useState(null)
  const [showBookingInfoModal, setShowBookingInfoModal] = useState(false)
  const [showBookingRejectModal, setShowBookingRejectModal] = useState(false)
  const [modalBookingDetails, setModalBookingDetails] = useState({id: null, action: () => {}})
  const Paragraph = ({ children }) => <p className='truncate'>{ children }</p>
  const DateSpan = ({ children }) => <span><FormattedDate value={new Date(children * 1000)} /> <FormattedTime value={new Date(children * 1000)} /></span>
  const ScheduleType = (entry) => {
    let booking = getBooking(entry);

    return (
      <div>
      {booking ? (
        <FormattedMessage {...messages.booking } />
      )  : (
        <FormattedMessage {...messages.unavailability } /> 
      )}
      </div>
    )
  }
  const Actions = (entry) => {
    let booking = getBooking(entry);

    return (
    <div className='flex space-x-2'>
        { !('dealId' in entry) &&
          <div className='space-x-2'>
            <Button variant="inline" onClick={() => setEvent(entry)} >
              <FormattedMessage {...messages.edit } />
            </Button>
            <Button variant="inline" state="negative" onClick={() => onDelete(entry.uid)} >
              <FormattedMessage {...messages.delete } />
            </Button>
          </div>
        }
        { booking &&
          <Button variant="inline" onClick={() => setModalBookingDetails({id: booking.uid, action: () => setShowBookingInfoModal(true)})} >
            <FormattedMessage {...messages.details } />
          </Button>
        }
        { runtime.currentUser.isSupplier() && booking && (booking.accepted == 0 && booking.rejected == 0) &&
          <div className='space-x-2'>
            <Button variant="solid" size="xs" onClick={() => bookingAction('accept', entry.uid, internalAvailabilities)} >
              <FormattedMessage {...messages.accept } />
            </Button>
            <Button variant="outline" size="xs" state="negative" onClick={() =>setModalBookingDetails({id: entry.uid, action: () => setShowBookingRejectModal(true)})} >
              <FormattedMessage {...messages.reject } />
            </Button>
          </div>
        }
    </div>)
  }

  useEffect(() => {
    if (modalBookingDetails.id && modalBookingDetails.action) {
        modalBookingDetails.action()
    }
  }, [modalBookingDetails])

  const getStatusChip = (entry) => {
    let booking = getBooking(entry);
    const statusChip = {
      accepted: {
        state: 'success',
        text: 'Accepted'
      },
      rejected: {
        state: 'error',
        text: 'Rejected'
      },
      pending: {
        state: 'warning',
        text: 'Pending'
      },
    }
    let status = null;
    if (booking) {
      if (+ booking.accepted) {
        status = statusChip.accepted
      } else if (+ booking.rejected) {
        status = statusChip.rejected
      } else {
        status = statusChip.pending
      }
    }

    return (
      <>
        {status &&
          <Chip size="sm" state={status.state}>
            <span className="capitalize">
              { status ? status.text : '' }
            </span>
          </Chip>
        }
      </>
      
    )
  }

  const getBooking = (entry) => {
    let booking = null; 
    if ('bookingDetails' in entry) {
      booking = entry.bookingDetails;
    } else if ('dealId' in entry) {
      booking = entry
    }
    return booking
  }

  useEffect(() => {
    if (bookings.length > 0) {
      availabilities.map((availability) => {
        if (availability.booking > 0) {
          let matchingBookingIndex = bookings.findIndex((booking) => booking.uid == availability.booking)
          if (matchingBookingIndex > -1) {
            availability.bookingDetails = bookings[matchingBookingIndex]
            bookings.splice(matchingBookingIndex, 1);
          }
        }
        return availability
      })

      setInternalAvailabilities(availabilities.concat(bookings).sort((a, b) => (
        a.startDate < b.startDate 
      )))
    } else {
      setInternalAvailabilities(availabilities.sort((a, b) => (
        a.startDate < b.startDate 
      )))
    }
    
  }, [bookings, availabilities])

  const onCreate = (type) => {
    const newEvent = {
      dateStart: new Date(new Date().setHours(12,0,0,0)).setDate(new Date().getDate() + 1) / 1000,
      dateEnd: new Date(new Date().setHours(12,0,0,0)).setDate(new Date().getDate() + 2) / 1000,
      message: '',
      booking: 0
    };

    if (type == 'booking') {
      newEvent.bookingDetails = {
        dealId: ''
      }
    }

    setEvent(newEvent)
    
    availabilities = [...availabilities, newEvent]
  }

  const onCancel = (prevEvent) => {
    if (!prevEvent.uid) {
      setInternalAvailabilities(internalAvailabilities.filter(({ uid }) => !!uid))
    }
    setEvent(null)
  }

  const onSaveInternal = (prevEvent, nextEvent) => {
    onSave(prevEvent, nextEvent)
    setEvent(null)
  }

  return (
    <div>
      { (internalAvailabilities.filter((entry) => entry.uid != undefined).length > 0 || noDataMessage !== '') &&
        <DataTable
          noDataMessage={noDataMessage}
          className="table-fixed"
          components={{
            Tr: ({ childProps = {}, ...innerProps }) => {
              return (
                <Table.Tr
                  {...innerProps }
                />
              )
            },
            Td: ({ childProps = {}, ...props }) => (
              <Table.Td
                {...props }
              />
            )
          }}
          head={{
            type: { sortable: true, label: 'Type' },
            dateStart: { sortable: true, label: 'From' },
            dateEnd: { sortable: true, label: 'To' },
            message: { sortable: true, label: 'Message' },
            status: { sortable: true, label: 'Status' },
            actions: { label: 'Actions' },
          }}
          body={{
            type: (entry) => (
              ScheduleType(entry)
            ),
            dateStart: DateSpan,
            dateEnd: DateSpan,
            message: Paragraph,
            status: (entry) => (
              getStatusChip(entry)
            ),
            actions: (entry) => (
              Actions(entry)
            )
          }}
          entries={internalAvailabilities.filter((entry) => entry.uid != undefined)}
        />
      }
      <div>
      {event &&
        <div>
          <InlineEventEditor
            booking={getBooking(event)}
            event={event}
            key={event.uid}
            onSaveInternal={(nextEvent) => onSaveInternal(event, nextEvent)}
            onCancel={(nextEvent) => onCancel(event, nextEvent)}
          />
        </div>
      }
      </div>

      { internalAvailabilities.length ? (
        <div className='flex space-x-2 justify-end'>
          {includeAvailabilityRequest &&
            <Button size="sm" className="float-right my-4" onClick={ () => event === null ? onCreate('availability') : undefined}>
              + <FormattedMessage {...messages.addAvailability } />
            </Button>
          }
          { runtime.currentUser.isCoordinator() && includeBookingRequest &&
            <Button size="sm" className="float-right my-4" onClick={ () => event === null ? onCreate('booking') : undefined}>
              + <FormattedMessage {...messages.addNewBooking } />
            </Button>
          }
        </div>
      ) : (
        <div className='flex space-x-2'>
          {includeAvailabilityRequest &&
            <Button variant="outline" className={`${runtime.currentUser.isCoordinator() && includeBookingRequest ? 'w-1/2' : 'w-full' } py-6`} onClick={ () => event === null ? onCreate('availability') : undefined}>
              + <FormattedMessage {...messages.addNewAvailability } />
            </Button>
          }
          { runtime.currentUser.isCoordinator() && includeBookingRequest && 
            <Button variant="outline" className="w-1/2 py-6" onClick={ () => event === null ? onCreate('booking') : undefined}>
              + <FormattedMessage {...messages.addNewBooking } />
            </Button>
          }
        </div>
      ) }
      <BookingInfoModal show={showBookingInfoModal} bookingId={modalBookingDetails.id} onClose={() => setShowBookingInfoModal(false)} />
      <BookingRejectModal show={showBookingRejectModal} onClose={() => setShowBookingRejectModal(false)} onReject={ (message) => {
          bookingAction('reject', modalBookingDetails.id, internalAvailabilities, message)
          setShowBookingRejectModal(false)
        }} />
    </div>
  )
}

export default AvailabilityDataTable
